import {
  BUSINESS_DETAILS_ROUTE,
  CONFIRMATION_ROUTE,
  PAY_MY_BILL_CONFIRMATION_ROUTE,
  PAYMENTS_ROUTE,
  QUOTE_ROUTE,
} from 'constants/routes';
import { AnalyticsTransactionType } from 'types/analytics';

import { RootState } from '../../store';
import { getLiabilityLimit } from './getSlices/getLiabilityLimit';
import { isPayMyBill } from './trackUtils';

/**
 * Defines the structure for page mappings.
 */
export interface PageMapping {
  condition: (pathname: string) => boolean;
  actions?: PageAction[];
  commonPayload?: Record<string, unknown>;
}

/**
 * Defines the structure for page actions.
 */
export interface PageAction {
  test?: RegExp | string;
  newPath?: string;
  dataPoints?: Record<string, unknown>;
  method?: (state: RootState) => void;
  ecommerce?: boolean;
}

/**
 * Defines common conditions for Pay My Bill.
 * @returns {boolean} - True if it is not the Pay My Bill page, otherwise false.
 */
export const isNotPayMyBill = () => !isPayMyBill();

/**
 * Defines rules for Pay My Bill.
 */
export const payMyBillRules: PageMapping = {
  condition: isPayMyBill,
  actions: [
    {
      test: /^\/pay-my-bill\/?$/,
      newPath: '/welcome',
      dataPoints: { transactionStartView: true, appStart: true },
    },
    {
      test: PAY_MY_BILL_CONFIRMATION_ROUTE,
      newPath: '/confirmation',
      dataPoints: { transactionComplete: true, appComplete: true },
    },
  ],
  commonPayload: { transactionType: AnalyticsTransactionType.renewalPayment },
};

/**
 * Defines page mappings for various routes.
 */
export const pageMappings: Record<string, PageMapping> = {
  // PAY MY BILL RULES
  payMyBill: payMyBillRules,

  // POLICY PAGE
  policy: {
    condition: (pathname) => /^\/policy\/.+/i.test(pathname) && isNotPayMyBill(),
    actions: [
      {
        newPath: '/policy', // Overriding PathName
      },
    ],
  },
  // BUSINESS DETAILS - QUOTE START
  businessDetails: {
    condition: (pathname) => pathname === BUSINESS_DETAILS_ROUTE && isNotPayMyBill(),
    actions: [
      {
        dataPoints: {
          quoteStartView: true, // QAL KPI Helper
          appStart: true, // QAL KPI Helper
        },
      },
    ],
  },
  // INDICATIVE QUOTE
  quote: {
    condition: (pathname) => pathname === QUOTE_ROUTE && isNotPayMyBill(),
    actions: [
      {
        dataPoints: {
          indicativeQuote: true, // QAL KPI Helper
        },
        method: getLiabilityLimit,
      },
    ],
  },
  // CUSTOMER HISTORY - QUOTE COMPLETE & BUY START VIEW
  Payments: {
    condition: (pathname) => pathname === PAYMENTS_ROUTE && isNotPayMyBill(),
    actions: [
      {
        dataPoints: {
          quoteComplete: true,
          buyStartView: true,
        },
      },
    ],
  },
  // CONFIRMATION - PURCHASE
  confirmation: {
    condition: (pathname: string) => pathname === CONFIRMATION_ROUTE && isNotPayMyBill(),
    actions: [
      {
        dataPoints: {
          purchase: true,
          appComplete: true,
        },
        ecommerce: true,
      },
    ],
  },
};
